import { useTranslation } from 'react-i18next'
import { NavLink, useLocation } from 'react-router-dom'
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
  useSidebar,
} from '@/components/ui/sidebar'
import { useAuth } from '@/auth/useAuth'
import { APP_NAME } from '@/components/layout/brand'
import { BrandMark } from '@/components/layout/BrandMark'
import { NavApps } from '@/components/layout/NavApps'
import { NavUser } from '@/components/layout/NavUser'
import { activeItem, navigation } from '@/components/layout/navigation'

export function AppSidebar() {
  const { t } = useTranslation()
  const { hasRole } = useAuth()
  const { pathname } = useLocation()
  // The very entry the header's breadcrumb names. One matcher for both, so a highlighted item
  // and the crumb above it can never disagree about which page this is.
  const current = activeItem(pathname)
  // On a phone or tablet the sidebar is a sheet over the page. Picking a page is the end of what
  // it was opened for, and a sheet left standing would hide the page that was just picked.
  const { setOpenMobile } = useSidebar()
  const closeSheet = () => setOpenMobile(false)

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton size="lg" asChild tooltip={APP_NAME}>
              <NavLink to="/" onClick={closeSheet}>
                {/* Larger than the menu icons below, as a logo should be; back to the button's
                    size when the sidebar collapses to icons, where it is only that wide. */}
                <BrandMark className="size-10 group-data-[collapsible=icon]:size-8" />
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-semibold">{APP_NAME}</span>
                  <span className="text-muted-foreground truncate text-xs">{t('app.tagline')}</span>
                </div>
              </NavLink>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      <SidebarContent>
        {/* A landmark of its own: the header's breadcrumb is the page's other navigation, and
            screen reader users should be able to tell the two apart. */}
        <SidebarGroup role="navigation" aria-label={t('nav.group')}>
          <SidebarGroupLabel>{t('nav.group')}</SidebarGroupLabel>
          <SidebarMenu>
            {navigation
              .filter((item) => !item.role || hasRole(item.role))
              .map((item) => (
                <SidebarMenuItem key={item.to}>
                  <SidebarMenuButton
                    asChild
                    isActive={item === current}
                    tooltip={t(item.labelKey)}
                  >
                    <NavLink to={item.to} end={item.end} onClick={closeSheet}>
                      <item.icon />
                      <span>{t(item.labelKey)}</span>
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
          </SidebarMenu>
        </SidebarGroup>
        <NavApps />
      </SidebarContent>

      <SidebarFooter>
        <NavUser />
      </SidebarFooter>

      {/* The drag handle along the sidebar edge — collapsing without hunting for the trigger.
          Labelled here because the component's own label is hard-coded English. */}
      <SidebarRail aria-label={t('nav.toggleSidebar')} title={t('nav.toggleSidebar')} />
    </Sidebar>
  )
}
